import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { BRAND, SERVICE_CATEGORIES } from "@/lib/data";
import { FOOTER } from "@/constants/testIds";
import { useTheme } from "@/hooks/useTheme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error(t("toast.invalidEmail"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success(t("toast.subscribed"));
      setEmail("");
    } catch (err) {
      toast.error(t("toast.subscribeError"));
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <footer data-testid={FOOTER.root} className="relative border-t border-ivory/[0.06] bg-ink-950">
      <div className="mx-auto max-w-[1440px] px-6 py-20 lg:px-10 lg:py-28">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={theme === "light" ? BRAND.logo.dark : BRAND.logo.light}
                alt="Ragi"
                className="h-12 w-auto object-contain"
                width="1200"
                height="296"
              />
            </div>
            <p className="mt-8 max-w-md font-display text-2xl leading-tight tracking-tight text-ivory">
              {t("footer.tagline")}
            </p>
            <form
              data-testid={FOOTER.newsletter}
              onSubmit={submit}
              className="mt-10 flex max-w-md items-center gap-3 border-b border-ivory/20 pb-3"
            >
              <input
                data-testid={FOOTER.newsletterInput}
                type="email"
                value={email}
                placeholder={t("footer.subscribePlaceholder")}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent font-sans text-sm text-ivory placeholder:text-ivory-dim focus:outline-none"
              />
              <button
                data-testid={FOOTER.newsletterSubmit}
                type="submit"
                disabled={loading}
                className="whitespace-nowrap font-sans text-[11px] uppercase tracking-widest text-gold hover:text-ivory transition-colors disabled:opacity-60"
              >
                {loading ? t("footer.sending") : t("footer.subscribe")}
              </button>
            </form>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-10">
            <FooterCol title={t("footer.explore")}>
              <FLink to="/work">{t("nav.work")}</FLink>
              <FLink to="/case-studies">{t("nav.cases")}</FLink>
              <FLink to="/insights">{t("nav.insights")}</FLink>
              <FLink to="/about">{t("nav.about")}</FLink>
              <FLink to="/founder">{t("nav.founder")}</FLink>
              <FLink to="/contact">{t("nav.contact")}</FLink>
            </FooterCol>
            <FooterCol title={t("footer.services")}>
              {SERVICE_CATEGORIES.slice(0, 5).map((c) => (
                <FLink key={c.slug} to={`/services#${c.slug}`}>
                  {c.label}
                </FLink>
              ))}
              <FLink to="/services">{t("footer.allServicesLink")}</FLink>
            </FooterCol>
            <FooterCol title={t("footer.studio")}>
              <div className="text-ivory-muted">
                Ragi
                <br />
                Bengaluru, India
              </div>
              <a
                href={`mailto:${BRAND.email}`}
                className="text-ivory hover:text-gold transition-colors"
              >
                {BRAND.email}
              </a>
              <a
                href={BRAND.phoneHref}
                className="text-ivory hover:text-gold transition-colors"
              >
                {t("nav.call")} · {BRAND.phone}
              </a>
              <a
                href={BRAND.meetingHref}
                className="text-gold hover:text-ivory transition-colors"
              >
                {t("nav.bookMeeting")} →
              </a>
            </FooterCol>
            <FooterCol title={t("footer.social")}>
              <a
                href={BRAND.socials.instagram}
                target="_blank"
                rel="noreferrer noopener"
                className="text-ivory hover:text-gold transition-colors"
              >
                Instagram
              </a>
              <a
                href={BRAND.socials.linkedin}
                target="_blank"
                rel="noreferrer noopener"
                className="text-ivory hover:text-gold transition-colors"
              >
                LinkedIn
              </a>
              <a
                href={BRAND.socials.behance}
                target="_blank"
                rel="noreferrer noopener"
                className="text-ivory hover:text-gold transition-colors"
              >
                Behance
              </a>
              <a
                href={BRAND.socials.dribbble}
                target="_blank"
                rel="noreferrer noopener"
                className="text-ivory hover:text-gold transition-colors"
              >
                Dribbble
              </a>
            </FooterCol>
          </div>
        </div>

        <div className="mt-20 flex flex-col items-start justify-between gap-6 border-t border-ivory/[0.06] pt-8 md:flex-row md:items-center">
          <div className="font-sans text-[11px] uppercase tracking-widest text-ivory-dim">
            © {year} Ragi Media. {t("footer.rights")}
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-sans text-[11px] uppercase tracking-widest text-ivory-dim">
            <Link to="/privacy" className="hover:text-gold">{t("footer.privacy")}</Link>
            <Link to="/terms" className="hover:text-gold">{t("footer.terms")}</Link>
            <Link to="/payment-policy" className="hover:text-gold">Payment</Link>
            <Link to="/refund-policy" className="hover:text-gold">Refunds</Link>
            <Link to="/cancellation-policy" className="hover:text-gold">Cancellation</Link>
            <Link to="/delivery-policy" className="hover:text-gold">Delivery</Link>
            <Link to="/consent" className="hover:text-gold">Consent</Link>
            <Link to="/compliance" className="hover:text-gold">Compliance</Link>
            <Link to="/pricing" className="hover:text-gold">Pricing</Link>
            <Link to="/sitemap" className="hover:text-gold">{t("footer.sitemap")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div className="font-sans text-[11px] uppercase tracking-widest text-gold">{title}</div>
      <div className="mt-5 flex flex-col gap-3 font-sans text-sm text-ivory-muted">{children}</div>
    </div>
  );
}

function FLink({ to, children }) {
  return (
    <Link to={to} className="text-ivory hover:text-gold transition-colors">
      {children}
    </Link>
  );
}
